import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import { useRef } from "react";

// Project details data - With actual screenshots and new projects
const projectsData = {
  intlrr: {
    title: "INTLRR",
    category: "EdTech Platform",
    year: "2024",
    client: "INTLRR Finance Education",
    duration: "6 Weeks",
    website: "https://intlrr.com",
    heroImage: "https://customer-assets.emergentagent.com/job_aakaar-studio/artifacts/cq7rzh1j_Intlrr.PNG",
    overview: "INTLRR is a premier EdTech platform providing cohort-based learning programs in finance. They offer specialized courses in Equity Research, Financial Modeling, Fixed Income, Credit Analysis, and Quantitative Finance—training over 10,000 students with industry mentors from top firms like IIM Nagpur, IIM Lucknow, and SIBM.",
    challenge: "The challenge was to create an engaging digital platform that would communicate the value of cohort-based finance education, showcase course offerings, display social proof through alumni success stories, and convert visitors into enrolled students.",
    solution: "We designed a modern, professional website with clear course showcases, compelling testimonials from IIM alumni, and streamlined enrollment flows. The design emphasizes credibility through statistics (10,000+ students, 92% completion rate) while maintaining an accessible, friendly feel.",
    services: ["Website Design", "UI/UX Design", "Development", "Conversion Optimization"],
    images: [
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
      "https://images.unsplash.com/photo-1635236190542-d43e4d4b9e4b?w=800&q=80",
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
    ],
    testimonial: {
      quote: "Aakaar Designs understood our vision for making finance education accessible and engaging. The website they created perfectly showcases our programs and has significantly improved our student enrollment rates.",
      author: "INTLRR Team"
    }
  },
  paingoriya: {
    title: "Paingoriya & Associates",
    category: "CA Firm Website",
    year: "2024",
    client: "Paingoriya & Associates, Chartered Accountants",
    duration: "4 Weeks",
    website: "https://paingoriyaandassociates.com",
    heroImage: "https://customer-assets.emergentagent.com/job_aakaar-studio/artifacts/okzz4rtw_paingoriya%26associates.PNG",
    overview: "Paingoriya & Associates is a trusted Chartered Accountancy firm established in 2013, headquartered in Agra. They offer comprehensive services including statutory audit, tax audit, ITR filing, GST services, company incorporation, and financial advisory—serving 500+ happy clients across India with 13+ years of experience.",
    challenge: "CA firm websites often feel dated and unapproachable. Our challenge was to create a professional yet warm digital presence that clearly communicates their extensive service offerings while building trust with potential clients seeking accounting and compliance services.",
    solution: "We crafted a clean, authoritative design that showcases their 30+ services across 9 categories. Strategic use of their tagline 'आपके वित्तीय सफर का साथी' (Your Financial Journey Partner) creates cultural connection. Easy contact options and clear service descriptions improve accessibility.",
    services: ["Website Design", "UI/UX Design", "Development", "Content Strategy"],
    images: [
      "https://images.unsplash.com/photo-1632152053988-e94d3d77829b?w=800&q=80",
      "https://images.unsplash.com/photo-1657727534685-36b09f84e193?w=800&q=80",
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80"
    ],
    testimonial: {
      quote: "Our new website has transformed how clients perceive our firm. We've received numerous compliments and inquiries from clients who found us online. The professional design truly reflects our 13+ years of expertise.",
      author: "Paingoriya & Associates"
    }
  },
  "alvron-partners": {
    title: "Alvron Partners",
    category: "Investment Banking",
    year: "2024",
    client: "Alvron Partners Investment Banking",
    duration: "5 Weeks",
    website: "https://alvronpartners.com",
    heroImage: "https://images.unsplash.com/photo-1761735486587-bcac08b15c79?w=1200&q=80",
    overview: "Alvron Partners is a boutique investment banking firm that specializes in raising capital for financial institutions. They provide comprehensive advisory services including wealth management consultancy, credit solutions, mergers & acquisitions, valuations, strategic advisory, and asset management.",
    challenge: "Creating a website for an investment banking firm requires balancing sophistication with approachability. The challenge was to convey trust, expertise, and professionalism while making complex financial services easy to understand for potential clients.",
    solution: "We developed a sleek, corporate website with clear service presentations, compelling value propositions, and professional imagery. The design emphasizes their boutique, client-centric approach while maintaining the gravitas expected in investment banking.",
    services: ["Website Design", "UI/UX Design", "Development", "Brand Strategy"],
    images: [
      "https://images.unsplash.com/photo-1574884280706-7342ca3d4231?w=800&q=80",
      "https://images.unsplash.com/photo-1565688527174-775059ac429c?w=800&q=80",
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80"
    ],
    testimonial: {
      quote: "Aakaar Designs created a website that perfectly represents our boutique investment banking firm. The professional design has helped us attract quality clients seeking personalized financial advisory services.",
      author: "Alvron Partners Team"
    }
  },
  "pyqguide": {
    title: "PYQ Guide",
    category: "Exam Prep Platform",
    year: "2024",
    client: "PYQ Guide Education",
    duration: "5 Weeks",
    website: "https://pyqguide.in",
    heroImage: "https://images.unsplash.com/photo-1756032433560-56547efed550?w=1200&q=80",
    overview: "PYQ Guide is an educational platform dedicated to helping students prepare for government exams. They provide comprehensive study materials, previous year question papers, mock tests, and strategic guidance for competitive exams including UPSC, SSC, Banking, and State PSC examinations.",
    challenge: "The exam preparation market is highly competitive. Our challenge was to create a platform that stands out by making study materials accessible, organized, and engaging while building trust with students preparing for crucial career-defining exams.",
    solution: "We designed a clean, focused platform with intuitive navigation, organized study materials, and progress tracking features. The design reduces cognitive load, making it easier for students to focus on learning rather than searching for content.",
    services: ["Website Design", "UI/UX Design", "Development", "Content Organization"],
    images: [
      "https://images.unsplash.com/photo-1654558535111-b4d71cf8a41f?w=800&q=80",
      "https://images.unsplash.com/photo-1649141325570-20e3986f20aa?w=800&q=80",
      "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&q=80"
    ],
    testimonial: {
      quote: "The website Aakaar Designs built for us has made our study materials much more accessible to students. The clean interface helps aspirants focus on what matters most - their preparation.",
      author: "PYQ Guide Team"
    }
  },
  "luxe-brand": {
    title: "Luxe Brand Identity",
    category: "Brand Identity",
    year: "2024",
    client: "Luxe Fashion House",
    duration: "6 Weeks",
    heroImage: "https://images.unsplash.com/photo-1759563869318-54434b2abc68?w=1200&q=80",
    overview: "A luxury fashion brand needed a complete brand identity that would position them in the premium market segment and appeal to discerning customers.",
    challenge: "Creating a brand identity that feels timeless yet contemporary, exclusive yet accessible—striking the perfect balance for the luxury market.",
    solution: "We developed a sophisticated visual system with custom typography, a refined color palette, and flexible brand applications that work across all touchpoints.",
    services: ["Brand Identity", "Logo Design", "Visual System", "Brand Guidelines"],
    images: [
      "https://images.unsplash.com/photo-1759563874682-30dc804d3257?w=800&q=80",
      "https://images.unsplash.com/photo-1759563874664-7d43971d36c2?w=800&q=80",
      "https://images.unsplash.com/photo-1759563874833-d8f97cef9d32?w=800&q=80"
    ],
    testimonial: {
      quote: "The brand identity Aakaar created perfectly captures our essence. It's sophisticated, memorable, and truly sets us apart.",
      author: "Luxe Creative Director"
    }
  },
  "tech-startup": {
    title: "Tech Startup Landing",
    category: "Landing Page",
    year: "2024",
    client: "TechVenture Inc.",
    duration: "2 Weeks",
    heroImage: "https://images.unsplash.com/photo-1720135885458-0af8193c4a0f?w=1200&q=80",
    overview: "A SaaS startup required a high-converting landing page to launch their innovative product and capture early adopter interest.",
    challenge: "Communicating complex technical features in an engaging, accessible way while driving conversions and building trust with potential users.",
    solution: "We created a dynamic landing page with clear value propositions, compelling visuals, and strategic CTAs. A/B testing optimized conversion rates.",
    services: ["Landing Page Design", "UI/UX Design", "Development", "Conversion Optimization"],
    images: [
      "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80",
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
      "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80"
    ],
    testimonial: {
      quote: "Our conversion rate increased by 40% after launching the new landing page. The design perfectly balances aesthetics with functionality.",
      author: "TechVenture CEO"
    }
  }
};

// Parallax Image Component
function ParallaxImage({ src, alt, className }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1.1, 1.05]);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y, scale }}
        className="w-full h-full object-cover"
      />
    </div>
  );
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const project = projectsData[projectId];
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.5]);

  if (!project) {
    return (
      <div className="bg-[#050505] min-h-screen pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-white mb-4">Project Not Found</h1>
          <Link to="/work" className="text-[#C5A059] hover:underline">
            Back to Work
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="project-detail-page">
      {/* Hero */}
      <section className="pt-24 md:pt-32">
        <div className="section-padding pb-8">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/work"
                className="inline-flex items-center gap-2 text-[#A1A1A1] text-sm hover:text-[#C5A059] transition-colors mb-8 group"
                data-testid="back-to-work"
              >
                <motion.span whileHover={{ x: -4 }} transition={{ duration: 0.2 }}>
                  <ArrowLeft size={16} />
                </motion.span>
                Back to Work
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
                {project.category}
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white mb-6">
                {project.title}
              </h1>
              <div className="flex flex-wrap gap-8 text-sm">
                <div>
                  <p className="text-[#A1A1A1] mb-1">Client</p>
                  <p className="text-white">{project.client}</p>
                </div>
                <div>
                  <p className="text-[#A1A1A1] mb-1">Year</p>
                  <p className="text-white">{project.year}</p>
                </div>
                <div>
                  <p className="text-[#A1A1A1] mb-1">Duration</p>
                  <p className="text-white">{project.duration}</p>
                </div>
                {project.website && (
                  <div>
                    <p className="text-[#A1A1A1] mb-1">Website</p>
                    <a
                      href={project.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#C5A059] hover:underline inline-flex items-center gap-1 group"
                      data-testid="project-website-link"
                    >
                      Visit Site 
                      <motion.span whileHover={{ x: 2, y: -2 }}>
                        <ExternalLink size={12} />
                      </motion.span>
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Hero Image with Parallax Zoom */}
        <motion.div
          ref={heroRef}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="px-6 md:px-12 lg:px-16"
        >
          <div className="max-w-7xl mx-auto overflow-hidden border border-white/10">
            <motion.div 
              style={{ scale: heroScale, opacity: heroOpacity }}
              className="aspect-[16/9] bg-[#0F0F0F]"
            >
              <img
                src={project.heroImage}
                alt={project.title}
                className="w-full h-full object-cover object-top"
                data-testid="project-hero-image"
              />
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <h2 className="font-heading text-2xl md:text-3xl text-white mb-6">Overview</h2>
              <p className="text-[#A1A1A1] text-base md:text-lg leading-relaxed">
                {project.overview}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <h3 className="font-heading text-xl text-white mb-4">Services</h3>
              <ul className="space-y-3">
                {project.services.map((service, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="text-[#A1A1A1] text-sm flex items-center gap-3"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059]" />
                    {service}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="section-padding border-t border-white/5">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
                The Challenge
              </p>
              <p className="text-[#A1A1A1] text-base leading-relaxed">
                {project.challenge}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
                The Solution
              </p>
              <p className="text-[#A1A1A1] text-base leading-relaxed">
                {project.solution}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Project Images with Parallax */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {project.images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className={index === 0 ? "md:col-span-2" : ""}
              >
                <ParallaxImage
                  src={image}
                  alt={`${project.title} - View ${index + 1}`}
                  className={`bg-[#0F0F0F] border border-white/5 ${
                    index === 0 ? "aspect-[16/9]" : "aspect-[4/3]"
                  }`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section-padding border-t border-white/5">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.p 
              className="font-accent text-2xl md:text-3xl italic text-white/90 leading-relaxed mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 1 }}
            >
              "{project.testimonial.quote}"
            </motion.p>
            <motion.p 
              className="text-[#C5A059]"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              {project.testimonial.author}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Next Project CTA */}
      <section className="section-padding border-t border-white/5">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#A1A1A1] uppercase tracking-[0.3em] text-xs mb-6">
              Ready to start your project?
            </p>
            <a
              href="https://wa.me/918006521008"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3"
              data-testid="project-detail-whatsapp-cta"
            >
              Let's Talk
              <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
